<script>
import Reactions from "@vvg/forum/js/mixins/reactions";
import Permissions from '@vvg/forum/js/mixins/permissions.js';
import ReactionForm from "@vvg/forum/vue/reaction/_form.vue";
import {Link} from "@inertiajs/vue3";

const MAX_THREAD_DEPTH = 2;

export default {
	name: "ReactionItem",
	components: {ReactionForm, Link},
	mixins: [Reactions, Permissions],
	props: {
		reaction: {
			type: Object,
			required: true
		},
		topicId: {
			type: Number,
			required: true
		}
	},
	emits: ['changed'],
	data() {
		return {
			isEditing: false,
			isReplying: false,
			localReaction: null,
			localChildren: [],
			loadingMoreChildren: false,
			childrenOffset: 0,
			childrenLimit: 5,
			hasMoreChildren: false,
			childrenTotal: 0,
		}
	},
	computed: {
		canManage() {
			return (this.isAuthenticated() && this.localReaction.user_id === this.$page.props?.auth?.user?.id) || this.hasRole('Administrator');
		},
		isDeleted() {
			return this.localReaction.deleted_at !== null;
		},
		ownDepth() {
			return this.localReaction.depth ?? 0;
		},
		isAtMaxDepth() {
			return this.ownDepth >= MAX_THREAD_DEPTH;
		},
		replyParentId() {

			return this.localReaction.reply_parent_id ?? this.localReaction.id;
		},
		childrenDepth() {
			return this.ownDepth + 1;
		},
		remainingChildren() {
			return Math.max(0, this.childrenTotal - this.localChildren.length);
		}
	},
	created() {
		this.initFromReaction(this.reaction);
	},
	methods: {
		initFromReaction(r) {
			this.localReaction = {...r};

			if (r.is_flat) {
				this.localChildren = [];
				this.childrenTotal = 0;
				this.childrenOffset = 0;
				this.hasMoreChildren = false;
				return;
			}

			this.localChildren = r.children ? r.children.slice() : [];
			// children_total mag nooit kleiner zijn dan het aantal al geladen children
			this.childrenTotal = Math.max(r.children_total ?? 0, this.localChildren.length);
			this.childrenOffset = this.localChildren.length;
			this.hasMoreChildren = this.childrenTotal > this.localChildren.length;
		},

		async onSaved(savedReaction) {
			this.isEditing = false;
			this.isReplying = false;

			if (!savedReaction) return;

			// eigen reactie bewerkt
			if (savedReaction.id === this.localReaction.id) {
				this.handleSelfUpdate(savedReaction);
				return;
			}

			// directe child van dit item
			if (savedReaction.parent_id === this.localReaction.id) {
				await this.handleNewChild(savedReaction);
				return;
			}

			// flat item: reply gaat naar een hogere parent,
			// geef door naar boven zodat de juiste parent het afhandelt
			this.$emit('changed', {action: 'child_added', reaction: savedReaction});
		},

		async handleNewChild(savedReaction) {
			const isFlat = this.childrenDepth > MAX_THREAD_DEPTH;

			const childForList = {
				...savedReaction,
				depth: this.childrenDepth,
				is_flat: isFlat,
				// Flat items moeten altijd naar deze node als reply-parent wijzen,
				// niet naar hun echte database-parent (die zelf ook flat kan zijn).
				reply_parent_id: isFlat ? this.localReaction.id : savedReaction.reply_parent_id,
				children: [],
				children_total: isFlat ? 0 : (savedReaction.children_total ?? 0),
			};

			if (this.hasMoreChildren) {
				// er zijn al verborgen reacties — laad alles op zodat
				// de nieuwe reactie zichtbaar is onderaan
				await this.loadAllRemainingChildren();
			}

			this.insertReactionLocally(this.localChildren, childForList);
			this.childrenTotal++;
			this.childrenOffset++;
			this.hasMoreChildren = this.childrenTotal > this.localChildren.length;

			// alleen naar boven emitten als we niet op max depth zitten
			if (!this.isAtMaxDepth) {
				this.$emit('changed', {action: 'child_added', reaction: savedReaction});
			}
		},

		async loadAllRemainingChildren() {
			// laad alle resterende children zodat de nieuwe reactie
			// altijd onderaan zichtbaar is en niet achter "meer laden" valt
			while (this.hasMoreChildren) {
				await this.loadMoreChildren();
			}
		},

		handleSelfUpdate(savedReaction) {
			this.localReaction = {...this.localReaction, ...savedReaction};
			this.$emit('changed', {
				action: 'updated',
				reaction: {
					...this.localReaction,
					children: this.localChildren,
					children_total: this.childrenTotal,
				}
			});
		},

		deleteReaction() {
			if (!confirm('Weet je zeker dat je deze reactie wilt verwijderen?')) return;

			const url = this.$page.props.forum_url + 'delete_reaction/';
			this.Ajax(url, 'POST', {id: this.localReaction.id}).done((response) => {
				this.showToast('Reactie verwijderd');
				this.localReaction = {...this.localReaction, ...response.reaction};
				this.$emit('changed', {
					action: 'updated',
					reaction: {
						...this.localReaction,
						children: this.localChildren,
						children_total: this.childrenTotal,
					}
				});
			});
		},

		onChildChanged(payload) {
			this.handleReactionChange(this.localChildren, payload, {
				onDeleted: () => {
					this.childrenTotal--;
					this.childrenOffset--;
				},
				onChildAdded: (childPayload) => {
					// Als wij op max depth zitten nemen we altijd alle child_added over:
					// elk event dat hier aankomt is per definitie afkomstig van een van
					// onze (geneste) flat nakomelingen.
					if (this.isAtMaxDepth) {
						this.handleNewChild(childPayload.reaction);
						return;
					}
					this.$emit('changed', childPayload);
				},
			});
		},

		async loadMoreChildren() {
			if (this.loadingMoreChildren || !this.hasMoreChildren) return;

			this.loadingMoreChildren = true;

			const url = this.buildChildReactionsUrl({
				parent_id: this.localReaction.id,
				limit: this.childrenLimit,
				offset: this.childrenOffset,
				depth: this.childrenDepth,
				flatten: this.childrenDepth > MAX_THREAD_DEPTH ? 1 : 0,
			});

			try {
				const response = await Promise.resolve(this.Ajax(url));
				this.localChildren = this.localChildren.concat(response.data);
				this.hasMoreChildren = response.has_more;
				this.childrenOffset = this.localChildren.length;
				this.childrenTotal = response.total;
			} catch (e) {
				console.error('loadMoreChildren mislukt:', e);
			} finally {
				this.loadingMoreChildren = false;
			}
		}
	}
}
</script>

<template>
	<li class="reaction-item">
		<div v-if="!isEditing">
			<div class="reaction-item__content">
				<div class="d-flex flex-row reaction-item__inner">
					<div class="reaction-item__avatar">
						<img
							v-if="localReaction.user?.profile_picture_url"
							:src="localReaction.user.profile_picture_url"
							alt="profielfoto"
							class="reaction-item__avatar-img"
						/>
						<span v-else>{{ (localReaction.user?.username ?? 'O').slice(0, 2).toUpperCase() }}</span>
					</div>
					<div>
						<div class="reaction-item__meta flex-column flex-md-row">
							<Link
								v-if="localReaction.user && $page.props.auth.user"
								:href="$page.props.forum_url + 'profiel/' + localReaction.user.id + '/'"
								class="reaction-item__user"
							>{{ localReaction.user.username }}</Link>
							<span v-else-if="localReaction.user" class="reaction-item__user">{{ localReaction.user.username }}</span>
							<span v-else class="reaction-item__user">Onbekend</span>
							<span class="reaction-item__sep d-none d-md-block">·</span>
							<span class="reaction-item__date">{{ localReaction.created_at_formatted }}</span>
						</div>

						<p v-if="!isDeleted" class="reaction-item__text">{{ localReaction.reaction }}</p>
						<p v-else class="reaction-item__deleted">Deze reactie is verwijderd.</p>

						<div class="reaction-item__actions flex-wrap">
							<button
								v-if="isAuthenticated() && !isDeleted"
								type="button"
								class="reaction-item__action-btn"
								@click="isReplying = !isReplying"
							>
								<i class="fa fa-reply"></i>
								{{ isReplying ? 'Annuleren' : 'Reageren' }}
							</button>
							<button
								v-if="canManage && !isDeleted"
								type="button"
								class="reaction-item__action-btn"
								@click="isEditing = true"
							>
								<i class="fa fa-pencil"></i>
								Bewerken
							</button>
							<button
								v-if="canManage && !isDeleted"
								type="button"
								class="reaction-item__action-btn reaction-item__action-btn--danger"
								@click="deleteReaction"
							>
								<i class="fa fa-trash-o"></i>
								Verwijderen
							</button>
						</div>
					</div>
				</div>

				<div v-if="isReplying" class="reaction-item__reply-form">
					<ReactionForm
						:topic-id="topicId"
						:parent-id="replyParentId"
						@saved="onSaved"
						@cancel="isReplying = false"
					/>
				</div>

				<ul v-if="localChildren.length" class="reaction-item__children list-unstyled">
					<ReactionItem
						v-for="child in localChildren"
						:key="child.id"
						:reaction="child"
						:topic-id="topicId"
						@changed="onChildChanged"
					/>
				</ul>

				<div v-if="hasMoreChildren" class="reaction-item__load-more">
					<button
						type="button"
						class="btn btn-outline-secondary btn-sm d-flex align-items-center gap-2 text-wrap text-left"
						:disabled="loadingMoreChildren"
						@click="loadMoreChildren"
					>
			            <span v-if="loadingMoreChildren">
			                <span class="spinner-border spinner-border-sm" role="status"></span>
			                Laden...
			            </span>
						<span v-else>
			                <i class="fa fa-plus-circle"></i>
			                Meer reacties ({{ remainingChildren }}&nbsp;resterend)
			            </span>
					</button>
				</div>
			</div>
		</div>

		<div v-if="isEditing" class="reaction-item__edit-form">
			<ReactionForm
				:topic-id="topicId"
				:parent-id="localReaction.parent_id"
				:reaction="localReaction"
				@saved="onSaved"
				@cancel="isEditing = false"
			/>
		</div>
	</li>
</template>